/**
 * Integração com o Salesforce via Web-to-Lead (sem RD Station).
 *
 * Constantes, mapas de picklist e montagem do payload compartilhados pelos
 * formulários que enviam lead direto ao SF:
 *  - src/js/blocks/sfLeadForm.js       (edify-premium, edify-play, parceiro-edify)
 *  - src/js/blocks/calculateEdifyv2.js (calculadora Edify v2)
 *
 * Paridade de campos/valores com o LeadFormModal do site novo (Astro):
 * site-edify-astro/src/components/functional/LeadFormModal.jsx
 */
import { getStoredUtms } from "./utmTracking";

// OID da org de produção + IDs (00N...) dos campos custom do Lead.
export const SF_OID = "00Das00000CjPUn";
export const SF_WEB_TO_LEAD_URL =
    "https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8";

export const SF_FIELDS = {
    cargoDoLead: "00NN400000G60tm", // Cargo_do_lead__c (picklist restrita: 4 cargos MQL)
    naturezaDoLead: "00NN400000BEegn", // Natureza_do_Lead__c
    intencaoBilingue: "00NN400000BEfTB", // Intencao_com_o_programa_bilingue__c
    rangeMensalidade: "00NN400000BZbzI", // range_mensalidade__c
    rangeAlunos: "00NN400000BZnh3", // range_quantidade_alunos__c
    bilingueAtual: "00NN400000D3NnT", // Bilingue_atual__c (multipicklist; 'EDIFY' = escola parceira)
    origemConversao: "00NN400000B5UXx", // Origem_da_conversao__c
    campanha: "00NN400000B5UXf", // Campanha__c (utm_campaign)
    publico: "00NN400000B5UY0", // Publico__c (utm_term, fallback utm_medium)
    anuncio: "00NN400000B5UXd", // Anuncio__c (utm_content)
};

// ATENÇÃO: Cargo_do_lead__c, Natureza_do_Lead__c, Intencao_com_o_programa_bilingue__c
// e Bilingue_atual__c são picklists RESTRITAS. Valor fora da lista não é ignorado:
// o Web-to-Lead descarta o LEAD INTEIRO, em silêncio (responde 200 mesmo assim).
// Por isso nada aqui é enviado sem passar por um mapa/lista branca, e os valores
// abaixo precisam ser reconferidos no describe do Lead sempre que a org mudar.
// Última conferência contra a org de produção: 2026-08-09.

// Todos os cargos do formulário estão ativos na picklist desde a revisão de ~2026-07-31
// (antes só os 4 cargos MQL eram aceitos).
export const SF_CARGOS_VALIDOS = [
    "Diretor(a)",
    "Coordenador(a)",
    "Mantenedor(a)",
    "Gestor(a)",
    "Professor(a)",
    "Secretário(a) Escolar",
    "Aluno(a)",
    "Pai/Responsável",
    "Não trabalho em escola",
];

export const SF_NATUREZA_MAP = {
    "Ensino básico privado": "Ensino básico privado",
    "Ensino básico privado confessional": "Ensino básico privado confessional",
    "Ensino básico público": "Ensino básico público",
    "Escola de Idiomas": "Escola de idiomas",
    "Outros (Ensino superior, pré-vestibular, técnico, etc.)":
        "Outros (Ensino superior, pré-vestibular, técnico, etc.)",
};

// As opções do formulário terminam em ponto final; os valores da picklist não.
export const SF_INTENCAO_MAP = {
    "Quero implementar ou trocar ainda neste ano escolar.":
        "Quero implementar ou trocar ainda neste ano escolar",
    "Planejo implementar ou trocar no próximo ano escolar.":
        "Planejo implementar ou trocar no próximo ano escolar",
    "Estou apenas pesquisando e quero conhecer.": "Estou apenas pesquisando e quero conhecer",
    "Não tenho interesse no momento.": "Não tenho interesse no momento",
};

// Valores das picklists de range usados pelo modal do site novo.
export const SF_RANGE_ALUNOS = [
    "Menos de 50 alunos",
    "51 a 100 alunos",
    "101 a 200 alunos",
    "201 a 400 alunos",
    "401 a 800 alunos",
    "801 a 1200 alunos",
    "Acima de 1201 alunos",
];

export const SF_RANGE_MENSALIDADE = [
    "Menos de R$ 300",
    "R$ 300 - R$ 449",
    "R$ 450 - R$ 799",
    "R$ 800 - R$ 1199",
    "Acima de R$ 1200",
];

/**
 * Converte "R$ 1.234,56" (máscara maskMoney) em Number.
 * Precisa remover o separador de milhar ANTES de trocar a vírgula decimal.
 */
export function parseMoney(value) {
    if (typeof value === "number") return value;
    const digits = String(value || "")
        .replace(/[^\d,.-]/g, "")
        .replace(/\./g, "")
        .replace(",", ".");
    const parsed = Number(digits);
    return Number.isFinite(parsed) ? parsed : 0;
}

/** Total de alunos (número livre) → faixa da picklist range_quantidade_alunos__c */
export function rangeAlunosFromTotal(total) {
    const n = Number(total);
    if (!Number.isFinite(n) || n <= 0) return "";
    if (n <= 50) return "Menos de 50 alunos";
    if (n <= 100) return "51 a 100 alunos";
    if (n <= 200) return "101 a 200 alunos";
    if (n <= 400) return "201 a 400 alunos";
    if (n <= 800) return "401 a 800 alunos";
    if (n <= 1200) return "801 a 1200 alunos";
    return "Acima de 1201 alunos";
}

/** Mensalidade em R$ (valor livre) → faixa da picklist range_mensalidade__c */
export function rangeMensalidadeFromValor(valor) {
    const n = parseMoney(valor);
    if (!Number.isFinite(n) || n <= 0) return "";
    if (n < 300) return "Menos de R$ 300";
    if (n < 450) return "R$ 300 - R$ 449";
    if (n < 800) return "R$ 450 - R$ 799";
    if (n < 1200) return "R$ 800 - R$ 1199";
    return "Acima de R$ 1200";
}

/**
 * Monta o payload do Web-to-Lead a partir das respostas normalizadas do form.
 *
 * @param {object} v
 * @param {string} v.name                nome completo ("Maria Silva")
 * @param {string} v.email
 * @param {string} v.mobile              telefone
 * @param {string} v.company             nome da escola
 * @param {string} v.city
 * @param {string} v.state               UF (sigla)
 * @param {string} v.origem              Origem_da_conversao__c
 * @param {string} [v.cargo]             resposta crua de "relação com a escola"
 * @param {string} [v.rede]              resposta crua de "rede de ensino"
 * @param {string} [v.momento]           resposta crua de "momento do bilíngue"
 * @param {string} [v.escolaParceira]    resposta crua de "é escola parceira?"
 * @param {string} [v.rangeAlunos]       valor já no formato da picklist
 * @param {string} [v.rangeMensalidade]  valor já no formato da picklist
 * @param {string[]} [v.descriptionLines] linhas extras da Description
 * @returns {URLSearchParams}
 */
export function buildSfLeadParams(v) {
    // Nome completo → FirstName / LastName
    const nameParts = String(v.name || "").trim().split(/\s+/);
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ");

    const params = new URLSearchParams();
    params.set("oid", SF_OID);
    params.set("retURL", window.location.origin);
    params.set("lead_source", "Inbound");
    params.set("first_name", firstName);
    params.set("last_name", lastName);
    params.set("email", v.email || "");
    params.set("mobile", v.mobile || "");
    params.set("company", v.company || "");
    params.set("city", v.city || "");
    params.set("state", v.state || "");
    params.set(SF_FIELDS.origemConversao, v.origem || "");

    if (v.rangeAlunos) params.set(SF_FIELDS.rangeAlunos, v.rangeAlunos);
    if (v.rangeMensalidade) params.set(SF_FIELDS.rangeMensalidade, v.rangeMensalidade);

    if (SF_CARGOS_VALIDOS.includes(v.cargo)) params.set(SF_FIELDS.cargoDoLead, v.cargo);

    const natureza = SF_NATUREZA_MAP[v.rede];
    if (natureza) params.set(SF_FIELDS.naturezaDoLead, natureza);

    const intencao = SF_INTENCAO_MAP[v.momento];
    if (intencao) params.set(SF_FIELDS.intencaoBilingue, intencao);

    if (String(v.escolaParceira || "").startsWith("Sim")) {
        params.set(SF_FIELDS.bilingueAtual, "EDIFY");
    }

    // UTMs persistidas (last-touch) → aba Marketing do Lead.
    // Taxonomia Meta Ads: campaign = Campanha, term/adset = Público, content/ad = Anúncio
    const utms = getStoredUtms();
    if (utms) {
        if (utms.utm_campaign) params.set(SF_FIELDS.campanha, utms.utm_campaign.slice(0, 255));
        const publico = utms.utm_term || utms.utm_medium;
        if (publico) params.set(SF_FIELDS.publico, publico.slice(0, 255));
        if (utms.utm_content) params.set(SF_FIELDS.anuncio, utms.utm_content.slice(0, 255));
    }

    // Respostas completas na Description: preserva o que as picklists
    // restritas descartam (cargo desqualificante, rede "Outros", etc.)
    const lines = (v.descriptionLines || []).slice();
    if (utms) lines.push("UTMs: " + new URLSearchParams(utms).toString());
    if (lines.length) params.set("description", lines.join("\n"));

    return params;
}

/**
 * Envia o payload ao Web-to-Lead.
 * O endpoint não responde CORS — `no-cors` envia e assume sucesso se não lançar.
 */
export function submitSfLead(params) {
    return fetch(SF_WEB_TO_LEAD_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: params.toString(),
    });
}

/**
 * Validadores portados do schema yup do modal do site novo.
 *
 * Sem jquery-validation de propósito: vários bundles da página carregam cópias
 * do plugin e a última execução substitui $.validator (com Rocket Loader/minify
 * a ordem nem é previsível), apagando métodos custom.
 */
export const sfValidators = {
    name: (v) => {
        if (!v) return "Nome é obrigatório";
        if (v.length < 3) return "Nome deve ter no mínimo 3 caracteres";
        if (!/^[a-zA-ZÀ-ÿ\s]+$/.test(v)) return "Nome deve conter apenas letras";
        if (v.trim().split(/\s+/).length < 2) return "Informe nome e sobrenome";
        return null;
    },
    // yup.email()/HTML5 aceitam domínio sem TLD (ex.: allan@gmail) — regex exige ".algo"
    email: (v) => {
        if (!v) return "Email é obrigatório";
        if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(v)) return "Email inválido";
        return null;
    },
    phone: (v) => {
        if (!v) return "Telefone é obrigatório";
        if (
            !/^(?:(?:\+|00)?(55)\s?)?(?:\(?([1-9][0-9])\)?\s?)?(?:((?:9\d|[2-9])\d{3})-?(\d{4}))$/.test(v)
        )
            return "Telefone inválido.";
        return null;
    },
    required: (message) => (v) => (v ? null : message),
    minLength: (min, message) => (v) => {
        if (!v) return message;
        return v.length < min ? message : null;
    },
};

export function showFieldError($field, message) {
    $field.addClass("error is-invalid");
    let $msg = $field.closest(".form-group").find(".sf-field-error");
    if (!$msg.length) {
        $msg = $('<div class="sf-field-error invalid-feedback" style="display:block"></div>').insertAfter(
            $field
        );
    }
    $msg.text(message).show();
}

export function clearFieldError($field) {
    $field.removeClass("error is-invalid");
    $field.closest(".form-group").find(".sf-field-error").hide();
}

/**
 * Valida os campos de `$scope` cujo `name` tem validador em `validators`.
 * Marca os erros na UI, foca o primeiro inválido e retorna true se tudo ok.
 */
export function validateFields($scope, validators) {
    let firstInvalid = null;
    Object.keys(validators).forEach((name) => {
        const $field = $scope.find("[name='" + name + "']");
        if (!$field.length) return;
        const message = validators[name](($field.val() || "").trim());
        if (message) {
            showFieldError($field, message);
            if (!firstInvalid) firstInvalid = $field;
        } else {
            clearFieldError($field);
        }
    });
    if (firstInvalid) firstInvalid.trigger("focus");
    return !firstInvalid;
}
